import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ACTIVE_STAFF_KEY, ACTIVE_STAFF_NAME_KEY } from "@/lib/active-staff";
import { applyBrandTheme } from "@/lib/brand-theme";
import {
  clearStaffAccessToken,
  consumeStaffLogoutReturn,
  markStaffLinkSession,
  saveStaffAccessToken,
  setStaffSessionSwitchPending,
} from "@/lib/staff-access-link";
import {
  getStaffLinkInfo,
  setPinByToken,
  loginWithPin,
} from "@/lib/api/staff-access.functions";

export const Route = createFileRoute("/acesso/$token")({
  head: () => ({
    meta: [
      { title: "Acesso rápido — FutBux" },
      { name: "description", content: "Entre no FutBux com seu PIN de colaborador." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Acesso rápido — FutBux" },
      { property: "og:description", content: "Entre no FutBux com seu PIN de colaborador." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: StaffAccessPage,
});

type Info = {
  staffId: string;
  name: string | null;
  photoUrl: string | null;
  role: string;
  hasPin: boolean;
  tcName: string | null;
  tcLogo: string | null;
  primaryColor?: string | null;
};

function StaffAccessPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const info = useServerFn(getStaffLinkInfo);
  const setPinFn = useServerFn(setPinByToken);
  const login = useServerFn(loginWithPin);

  const [data, setData] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [retryIn, setRetryIn] = useState(0);

  // Contagem regressiva simples entre tentativas de revalidação do link.
  useEffect(() => {
    if (retryIn <= 0) return;
    const id = setTimeout(() => setRetryIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [retryIn]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      // Vincula o aparelho a este link ANTES de qualquer sessão: se o colaborador
      // sair (ou a sessão expirar), ele volta para esta mesma tela de PIN.
      saveStaffAccessToken(token);
      const returnedFromStaffLogout = consumeStaffLogoutReturn();
      try {
        // O link público é validado antes de tocar na sessão armazenada. Assim,
        // SIGNED_OUT/TOKEN_REFRESHED não pode transformar uma troca de sessão em
        // uma falsa mensagem de link revogado.
        let res: any = null;
        let lastError: unknown = null;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            res = await info({ data: { token } });
            break;
          } catch (requestError) {
            lastError = requestError;
            if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 250));
          }
        }
        if (!res) throw lastError;
        if (cancelled) return;
        if (res.ok === false) {
          // Link morto: esquecer o token para não prender o aparelho nesta tela.
          clearStaffAccessToken();
          setData(null);
          setError(res.message as string);
          return;
        }
        setError(null);
        setData(res);
        applyBrandTheme(res.primaryColor);

        if (!returnedFromStaffLogout) {
          try {
            const { data: current } = await supabase.auth.getSession();
            const currentUserId = current.session?.user?.id;
            if (currentUserId === res.staffId) {
              localStorage.setItem(ACTIVE_STAFF_KEY, res.staffId);
              localStorage.setItem(ACTIVE_STAFF_NAME_KEY, res.name ?? "");
              markStaffLinkSession();
              navigate({ to: "/dashboard", replace: true });
              return;
            }
            if (current.session) {
              localStorage.removeItem(ACTIVE_STAFF_KEY);
              localStorage.removeItem(ACTIVE_STAFF_NAME_KEY);
              setStaffSessionSwitchPending(true);
              try {
                // A troca é somente neste navegador; não derruba outras sessões
                // válidas do proprietário ou do colaborador em outros aparelhos.
                await supabase.auth.signOut({ scope: "local" });
              } finally {
                setStaffSessionSwitchPending(false);
              }
            }
          } catch {
            setStaffSessionSwitchPending(false);
            // Sessão anterior inválida não altera o resultado da validação pública.
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        setData(null);
        setError(e?.message || "Link inválido.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setBusy(true);
    try {
      if (!data.hasPin) {
        if (!/^\d{4,6}$/.test(pin)) throw new Error("O PIN deve ter de 4 a 6 dígitos.");
        if (pin !== pinConfirm) throw new Error("Os PINs não conferem.");
        await setPinFn({ data: { token, pin } });
      }
      const res = await login({ data: { token, pin } });
      await supabase.auth.setSession({
        access_token: res.accessToken,
        refresh_token: res.refreshToken,
      });
      saveStaffAccessToken(token);
      markStaffLinkSession();
      localStorage.setItem(ACTIVE_STAFF_KEY, res.staff.id);
      localStorage.setItem(ACTIVE_STAFF_NAME_KEY, res.staff.name ?? "");
      toast.success(`Bem-vindo, ${res.staff.name ?? "colaborador"}!`);
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      toast.error(err?.message || "Não foi possível entrar.");
      setPin("");
      setPinConfirm("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="p-8 space-y-6 text-center">
          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <>
              <h1 className="text-lg font-semibold">Link indisponível</h1>
              <p className="text-sm text-muted-foreground">
                Após sair do app, o link pode levar até 5 minutos para ser revalidado. Aguarde
                alguns instantes e tente novamente.
              </p>

              <Button
                className="w-full"
                disabled={retryIn > 0}
                onClick={() => {
                  setRetryIn(30);
                  setAttempt((n) => n + 1);
                }}
              >
                {retryIn > 0
                  ? `Aguarde ${retryIn}s para tentar de novo`
                  : "Aguarde até 5 minutos e tente novamente"}
              </Button>
            </>
          ) : data ? (
            <>
              {data.tcLogo ? (
                <img
                  src={data.tcLogo}
                  alt={data.tcName ?? "Centro de treinamento"}
                  className="h-12 mx-auto object-contain"
                  loading="lazy"
                />
              ) : (
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {data.tcName}
                </p>
              )}

              <div className="flex flex-col items-center gap-3">
                <Avatar className="h-20 w-20">
                  {data.photoUrl ? <AvatarImage src={data.photoUrl} alt={data.name ?? ""} /> : null}
                  <AvatarFallback className="text-2xl font-bold bg-primary/10 text-primary">
                    {data.name?.charAt(0).toUpperCase() ?? "?"}
                  </AvatarFallback>
                </Avatar>
                <h1 className="text-xl font-bold leading-tight">{data.name}</h1>
              </div>

              <form onSubmit={onSubmit} className="space-y-4 text-left">
                <div className="space-y-2">
                  <Label htmlFor="pin">
                    {data.hasPin ? "Digite seu PIN" : "Crie seu PIN (4 a 6 dígitos)"}
                  </Label>
                  <Input
                    id="pin"
                    inputMode="numeric"
                    autoFocus
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-2xl tracking-[0.5em]"
                    type="password"
                  />
                </div>
                {!data.hasPin && (
                  <div className="space-y-2">
                    <Label htmlFor="pin2">Confirme o PIN</Label>
                    <Input
                      id="pin2"
                      inputMode="numeric"
                      maxLength={6}
                      value={pinConfirm}
                      onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))}
                      className="text-center text-2xl tracking-[0.5em]"
                      type="password"
                    />
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={busy || pin.length < 4}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
                </Button>
              </form>

              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Acesso protegido por PIN
              </p>
            </>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}
